import { Head } from "@inertiajs/react"
import {
  CheckCheck,
  Coins,
  Gift,
  Inbox,
  Package,
  Sparkles,
  Truck,
  XCircle,
} from "lucide-react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { RedemptionFilterPills } from "@/components/rewards-redemptions/redemption-filter-pills"
import type { StatusFilter } from "@/components/rewards-redemptions/redemption-filter-pills"
import { RedemptionRowActions } from "@/components/rewards-redemptions/redemption-row-actions"
import { RewardsTabStrip } from "@/components/rewards-redemptions/rewards-tab-strip"
import { ShipmentAddressBlock } from "@/components/shipments/shipment-address-block"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import AppLayout from "@/layouts/app-layout"
import { toAppLocale } from "@/lib/i18n/locales"
import { formatStrictDistance } from "@/lib/relative-time"
import { cn } from "@/lib/utils"
import type { Locale } from "@/types"
import type { ShipmentAddress, ShipmentContact } from "@/types/shipments"

/**
 * OKS-61 — Brand-side Redemption fulfillment queue.
 *
 * Hosts the queue table (`Table` ≥ md, card stack < md) at the path
 * `/brand/rewards/redemptions`. Tab-strip companion to the existing
 * Catalogue page at `/brand/rewards` (NO new sidebar slot, per
 * UX-OQ-1).
 *
 * UX states covered (UX Spec §6):
 *   - **Loading** — Inertia SSR resolves first paint. No skeleton on
 *     subsequent visits — Inertia preserves the table from the prior
 *     load while the next props resolve.
 *   - **Empty** — branched copy per filter:
 *       * `?status=all` count 0 → full-bleed `Card` with `Inbox` icon
 *         and CTA back to the Catalogue.
 *       * `?status=pending|fulfilled|cancelled` count 0 (with other
 *         filters > 0) → inline single-line per-filter empty state.
 *   - **Error** — page-fetch failure handled by Inertia's default
 *     error overlay; in-dialog errors handled by the dialog
 *     components (UX Spec §4 + §5).
 *   - **Success** — Inertia visit closes the dialog (parent
 *     onSuccess) + table re-renders with the row's status flipped
 *     and the count chips decremented (server-rendered counts).
 *   - **Edge** — every state from UX Spec §6 edges:
 *       * former-crew tag for non-active memberships
 *       * em-dash for terminal-row actions cell
 *       * humanised terminal-timestamp under terminal status badge
 *       * brand-name prefix on admin no-act-as cross-brand view
 *       * mobile card stack < md
 */
// OKS-563 — mirrors `Redemption::STATUSES` / `TERMINAL_STATUSES`. Nothing
// cross-checks Ruby against TS, so this is a parallel convention kept by
// review, not a derivation.
type RedemptionStatus = "pending" | "shipped" | "fulfilled" | "cancelled"

const TERMINAL_STATUSES: readonly RedemptionStatus[] = [
  "fulfilled",
  "cancelled",
] as const

// ⚠️ Was `status !== "pending"` at BOTH call sites below. That negation
// classified `shipped` as terminal the moment the state existed, which
// stripped the row's kebab and made a paid-for redemption read as
// finished — PRD R4's stranding. Enumerated positively so a future state
// stays actionable (visible to the brand) by default.
function isTerminalStatus(status: RedemptionStatus): boolean {
  return TERMINAL_STATUSES.includes(status)
}

interface RedemptionRow {
  id: number
  status: RedemptionStatus
  coin_cost_snapshot: number
  created_at: string | null
  shipped_at: string | null
  tracking_number: string | null
  fulfilled_at: string | null
  cancelled_at: string | null
  /** OKS-997 — the creator's receipt assertion (PRD D6). A flag, not a status. */
  received_at: string | null
  creator: {
    name: string | null
    membership_status: string | null
    brand_name: string | null
  }
  reward: {
    id: number | null
    title: string | null
    kind: "product" | "experience" | "custom" | null
    unlimited_stock: boolean
  }
  /**
   * OKS-985 — the gated destination (UX Spec §2.3).
   *
   * ⚠️ ABSENT, not null, outside the reveal window — the key is missing from
   * the payload entirely for anything that is not a pending `gear`
   * redemption. Branch on PRESENCE (`"shipping_address" in row` or the
   * optional being `undefined`), NEVER on truthiness of a nested field: a
   * present-and-null shape would satisfy a truthiness check while shipping
   * the leak, and the server deliberately refuses to produce it.
   *
   * ⚠️ When absent, render NOTHING — no empty box, no dash, no "hidden"
   * placeholder. The row is simply shorter, and that is the correct signal
   * (UX Spec §2.3).
   */
  shipping_address?: ShipmentAddress
  /**
   * OKS-991 — email + phone, disclosed by the SAME gate as the address and
   * absent alongside it (PRD R6: the payload widens uniformly across all
   * three of `Shipments::AddressPayload`'s consumers). This page inherits the
   * two contact lines from `ShipmentAddressBlock` with no bespoke layout.
   */
  contact?: ShipmentContact
}

interface RedemptionsIndexProps {
  redemptions: RedemptionRow[]
  active_status: StatusFilter
  counts: Record<StatusFilter, number>
  filters: readonly StatusFilter[]
  cross_brand_admin: boolean
}

const KIND_ICON = {
  product: Package,
  experience: Sparkles,
  custom: Gift,
} as const

const STATUS_BADGE_VARIANT: Record<
  RedemptionStatus,
  "default" | "secondary" | "outline"
> = {
  pending: "secondary",
  shipped: "secondary",
  fulfilled: "default",
  cancelled: "outline",
}

const STATUS_DOT_CLASS: Record<RedemptionStatus, string> = {
  pending: "bg-amber-500",
  shipped: "bg-blue-500",
  fulfilled: "bg-emerald-500",
  cancelled: "bg-muted-foreground",
}

export default function RedemptionsIndex({
  redemptions,
  active_status,
  counts,
  cross_brand_admin,
}: RedemptionsIndexProps) {
  const { t, i18n } = useTranslation()
  // Same derivation as `Creator/MissionDetail.tsx` — the address block needs a
  // narrowed `Locale`, and the default is FR.
  const locale = toAppLocale(i18n.language)

  return (
    <AppLayout>
      <Head title={t("rewards.redemptions.head_title")} />
      <div className="flex flex-col gap-6 p-6">
        <RewardsTabStrip
          activeTab="redemptions"
          pendingCount={counts.pending}
        />

        <header className="space-y-0.5">
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">
            {t("rewards.redemptions.index.title")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("rewards.redemptions.index.subtitle")}
          </p>
        </header>

        <RedemptionFilterPills activeStatus={active_status} counts={counts} />

        {/* Empty branches (UX Spec §6).
            - "all-zero" Card (`redemptions-empty-state-all`) only when
              the user is explicitly on `?status=all` AND counts.all === 0.
              That's the "you've never had any" surface with the
              Catalogue CTA — irrelevant when the user is filtered to a
              specific status (they're asking a more specific question).
            - Per-filter empty (`redemptions-empty-state-{status}`) for
              any other empty render — gives the brand admin contextual
              "all caught up" / "no fulfilled yet" / "no cancelled"
              copy that matches the active filter. Lands on default
              `?status=pending` even when the brand has zero history
              (lets the brand know they're on the work queue). */}
        {redemptions.length === 0 ? (
          active_status === "all" && counts.all === 0 ? (
            <FullEmptyState />
          ) : (
            <PerFilterEmptyState status={active_status} />
          )
        ) : (
          <>
            <RedemptionsTable
              redemptions={redemptions}
              crossBrandAdmin={cross_brand_admin}
              locale={locale}
            />
            <RedemptionsCardStack
              redemptions={redemptions}
              crossBrandAdmin={cross_brand_admin}
              locale={locale}
            />
          </>
        )}
      </div>
    </AppLayout>
  )
}

function FullEmptyState() {
  const { t } = useTranslation()
  return (
    <Card className="border-dashed" data-testid="redemptions-empty-state-all">
      <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
        <Inbox className="text-muted-foreground size-12" aria-hidden="true" />
        <div className="space-y-2">
          <h2 className="text-lg font-medium">
            {t("rewards.redemptions.empty.all.title")}
          </h2>
          <p className="text-muted-foreground max-w-md text-sm">
            {t("rewards.redemptions.empty.all.body")}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

function PerFilterEmptyState({ status }: { status: StatusFilter }) {
  const { t } = useTranslation()

  // `?status=all` with counts.all > 0 but redemptions.length === 0
  // shouldn't happen (we computed counts from the same scope). Render
  // the All copy defensively.
  if (status === "all") {
    return <FullEmptyState />
  }

  // OKS-563 — `shipped` needs its own arm. The chain ends in XCircle
  // (the CANCELLED icon), so without this a "No shipped redemptions"
  // empty state would render under a cancellation glyph.
  const Icon =
    status === "pending"
      ? CheckCheck
      : status === "shipped"
        ? Truck
        : status === "fulfilled"
          ? Gift
          : XCircle

  return (
    <div
      className="flex items-center justify-center gap-2 rounded-lg border border-dashed p-12 text-center"
      data-testid={`redemptions-empty-state-${status}`}
    >
      <Icon className="text-muted-foreground size-5" aria-hidden="true" />
      <p className="text-muted-foreground text-sm">
        {t(`rewards.redemptions.empty.${status}`)}
      </p>
    </div>
  )
}

interface TableProps {
  locale: Locale
  redemptions: RedemptionRow[]
  crossBrandAdmin: boolean
}

function RedemptionsTable({
  redemptions,
  crossBrandAdmin,
  locale,
}: TableProps) {
  const { t } = useTranslation()

  return (
    <div className="hidden md:block">
      <Table data-testid="redemptions-table">
        <TableHeader>
          <TableRow>
            <TableHead>{t("rewards.redemptions.table.creator")}</TableHead>
            <TableHead>{t("rewards.redemptions.table.reward")}</TableHead>
            {/* OKS-985 — the destination column (UX Spec §2.3). Present as a
                header always; the CELL renders nothing when the key is
                absent, so a non-pending row is simply shorter. */}
            <TableHead>{t("rewards.redemptions.table.destination")}</TableHead>
            <TableHead className="w-[120px]">
              {t("rewards.redemptions.table.cost")}
            </TableHead>
            <TableHead className="w-[140px]">
              {t("rewards.redemptions.table.created")}
            </TableHead>
            <TableHead className="w-[180px]">
              {t("rewards.redemptions.table.status")}
            </TableHead>
            <TableHead className="w-[80px] text-right">
              <span className="sr-only">
                {t("rewards.redemptions.table.actions")}
              </span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {redemptions.map((redemption) => (
            <RedemptionTableRow
              key={redemption.id}
              redemption={redemption}
              crossBrandAdmin={crossBrandAdmin}
              locale={locale}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

interface RedemptionRowProps {
  redemption: RedemptionRow
  crossBrandAdmin: boolean
}

// ⚠️ Extends rather than widening `RedemptionRowProps`, which `CreatorCell`
// also uses. Adding `locale` there would force a prop on a component that has
// no use for it — and a required prop nobody needs is one every future caller
// has to thread through for no reason.
interface RedemptionTableRowProps extends RedemptionRowProps {
  locale: Locale
}

function RedemptionTableRow({
  redemption,
  crossBrandAdmin,
  locale,
}: RedemptionTableRowProps) {
  const { t } = useTranslation()
  const isTerminal = isTerminalStatus(redemption.status)

  return (
    <TableRow
      data-testid="redemption-table-row"
      data-redemption-id={redemption.id}
      data-status={redemption.status}
    >
      <TableCell>
        <CreatorCell
          redemption={redemption}
          crossBrandAdmin={crossBrandAdmin}
        />
      </TableCell>
      <TableCell>
        <RewardCell redemption={redemption} />
      </TableCell>
      <TableCell>
        {/* ⚠️ Branch on PRESENCE, never on truthiness of a nested field. The
            server omits the key entirely outside the pending gear window, and
            when it is absent we render NOTHING — no empty box, no dash, no
            "hidden" placeholder (UX Spec §2.3). */}
        {redemption.shipping_address ? (
          <ShipmentAddressBlock
            address={redemption.shipping_address}
            creatorLabel={redemption.creator.name ?? ""}
            locale={locale}
            contact={redemption.contact}
            testIdPrefix="redemption-row"
          />
        ) : null}
      </TableCell>
      <TableCell className="tabular-nums">
        <span className="inline-flex items-center gap-1.5">
          {redemption.coin_cost_snapshot}
          <Coins className="size-4" aria-hidden="true" />
          <span className="sr-only">
            {t("rewards.redemptions.table.coin_unit")}
          </span>
        </span>
      </TableCell>
      <TableCell>
        <RelativeTime iso={redemption.created_at} />
      </TableCell>
      <TableCell>
        <StatusCell redemption={redemption} />
      </TableCell>
      <TableCell className="text-right">
        {isTerminal || crossBrandAdmin ? (
          <span className="text-muted-foreground" aria-hidden="true">
            —
          </span>
        ) : (
          <RedemptionRowActions
            redemptionId={redemption.id}
            creatorName={redemption.creator.name ?? ""}
            rewardTitle={redemption.reward.title ?? ""}
            coinCost={redemption.coin_cost_snapshot}
            rewardIsUnlimited={redemption.reward.unlimited_stock}
            status={redemption.status}
          />
        )}
      </TableCell>
    </TableRow>
  )
}

function RedemptionsCardStack({
  redemptions,
  crossBrandAdmin,
  locale,
}: TableProps) {
  const { t } = useTranslation()
  return (
    <div className="grid gap-3 md:hidden" data-testid="redemptions-card-stack">
      {redemptions.map((redemption) => {
        const isTerminal = isTerminalStatus(redemption.status)
        return (
          <Card
            key={redemption.id}
            data-testid="redemption-card"
            data-redemption-id={redemption.id}
            data-status={redemption.status}
          >
            <CardContent className="space-y-3 pt-4">
              <div className="flex items-start justify-between gap-2">
                <CreatorCell
                  redemption={redemption}
                  crossBrandAdmin={crossBrandAdmin}
                />
                {isTerminal || crossBrandAdmin ? (
                  <span
                    className="text-muted-foreground self-center"
                    aria-hidden="true"
                  >
                    —
                  </span>
                ) : (
                  <RedemptionRowActions
                    redemptionId={redemption.id}
                    creatorName={redemption.creator.name ?? ""}
                    rewardTitle={redemption.reward.title ?? ""}
                    coinCost={redemption.coin_cost_snapshot}
                    rewardIsUnlimited={redemption.reward.unlimited_stock}
                    status={redemption.status}
                  />
                )}
              </div>
              <RewardCell redemption={redemption} />
              {/* OKS-985 — <md the destination stacks UNDER the reward line
                  rather than taking a column (UX Spec §2.3). Absent key ⇒
                  nothing renders, so the card is simply shorter. */}
              {redemption.shipping_address ? (
                <ShipmentAddressBlock
                  address={redemption.shipping_address}
                  creatorLabel={redemption.creator.name ?? ""}
                  locale={locale}
                  contact={redemption.contact}
                  testIdPrefix="redemption-row"
                />
              ) : null}
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-sm tabular-nums">
                  {redemption.coin_cost_snapshot}
                  <Coins className="size-4" aria-hidden="true" />
                  <span className="sr-only">
                    {t("rewards.redemptions.table.coin_unit")}
                  </span>
                </span>
                <RelativeTime iso={redemption.created_at} />
              </div>
              <StatusCell redemption={redemption} />
            </CardContent>
          </Card>
        )
      })}
    </div>
  )
}

function CreatorCell({ redemption, crossBrandAdmin }: RedemptionRowProps) {
  const { t } = useTranslation()
  const name = redemption.creator.name ?? "—"
  const initials = useMemo(() => computeInitials(name), [name])
  const isFormerCrew =
    redemption.creator.membership_status !== null &&
    redemption.creator.membership_status !== "active"

  return (
    <div className="flex items-center gap-2">
      <Avatar className="size-8">
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>
      <div className="flex flex-col">
        <span className="line-clamp-1" title={name}>
          {crossBrandAdmin && redemption.creator.brand_name ? (
            <>
              <span className="text-muted-foreground">
                [{redemption.creator.brand_name}]
              </span>{" "}
              {name}
            </>
          ) : (
            name
          )}
        </span>
        {isFormerCrew ? (
          <span className="text-muted-foreground text-xs">
            {t("rewards.redemptions.table.former_crew")}
          </span>
        ) : null}
      </div>
    </div>
  )
}

function RewardCell({ redemption }: { redemption: RedemptionRow }) {
  const kind = redemption.reward.kind
  const Icon = kind ? KIND_ICON[kind] : Gift
  const title = redemption.reward.title ?? "—"
  return (
    <div className="flex items-center gap-2">
      <Icon className="text-muted-foreground size-4" aria-hidden="true" />
      <span className="line-clamp-1" title={title}>
        {title}
      </span>
    </div>
  )
}

function StatusCell({ redemption }: { redemption: RedemptionRow }) {
  const { t, i18n } = useTranslation()
  const locale = toAppLocale(i18n.language)
  const status = redemption.status
  // OKS-563 — POSITIVE equality chains falling to `null`, so an unknown
  // state degrades to "no second line" rather than a wrong one. Kept in
  // this shape deliberately: do NOT convert to a negation while fixing
  // negations elsewhere in this file.
  const terminalAt =
    status === "fulfilled"
      ? redemption.fulfilled_at
      : status === "cancelled"
        ? redemption.cancelled_at
        : status === "shipped"
          ? redemption.shipped_at
          : null
  const terminalKey =
    status === "fulfilled"
      ? "rewards.redemptions.status.fulfilled_at"
      : status === "cancelled"
        ? "rewards.redemptions.status.cancelled_at"
        : status === "shipped"
          ? "rewards.redemptions.status.shipped_at"
          : null

  return (
    <div className="flex flex-col gap-0.5">
      <Badge
        variant={STATUS_BADGE_VARIANT[status]}
        className="w-fit"
        aria-label={t("rewards.redemptions.status.status_aria", {
          status: t(`rewards.redemptions.status.${status}`),
        })}
        data-testid={`redemption-status-badge-${status}`}
      >
        <span
          className={cn("mr-1.5 size-2 rounded-full", STATUS_DOT_CLASS[status])}
          aria-hidden="true"
        />
        {t(`rewards.redemptions.status.${status}`)}
      </Badge>
      {terminalAt && terminalKey ? (
        <span className="text-muted-foreground text-xs">
          {t(terminalKey, {
            when: formatStrictDistance(terminalAt, locale, { addSuffix: true }),
          })}
        </span>
      ) : null}
      {/* OKS-997 — the brand reads the creator's receipt flag (PRD D6). An
          ADDITIONAL sub-line: it does NOT replace « Expédiée Y » above, because
          the two record different facts (when we sent it / when they said it
          arrived) and a shipped-but-unreceived row must stay distinguishable
          from a received one.

          ⚠️ Deliberately NOT folded into the `terminalAt`/`terminalKey`
          positive-equality chains. Those degrade to "no second line" on an
          unknown status by design; receipt is orthogonal to status and renders
          on `shipped` AND on a `fulfilled`-after-receipt row. */}
      {redemption.received_at ? (
        <span
          className="text-muted-foreground text-xs"
          data-testid="redemption-row-received"
        >
          {t("rewards.redemptions.status.received_at", {
            when: formatStrictDistance(redemption.received_at, locale, {
              addSuffix: true,
            }),
          })}
        </span>
      ) : null}
      {/* OKS-563 — tracking under the status, shipped rows only (UX Spec
          §2.1). Truncated on narrow columns with the full value on
          `title`; a cancelled-after-shipped row keeps the reference in
          the DB (PRD R6 audit) but must not display it. */}
      {status === "shipped" && redemption.tracking_number ? (
        <span
          className="text-muted-foreground max-w-[16ch] truncate font-mono text-xs lg:max-w-none"
          title={redemption.tracking_number}
          data-testid="redemption-status-tracking"
        >
          {t("rewards.redemptions.status.tracking", {
            number: redemption.tracking_number,
          })}
        </span>
      ) : null}
    </div>
  )
}

function RelativeTime({ iso }: { iso: string | null }) {
  const { i18n } = useTranslation()
  if (iso === null) {
    return <span className="text-muted-foreground text-sm">—</span>
  }
  const date = new Date(iso)
  return (
    <span
      className="text-muted-foreground text-sm"
      title={date.toISOString()}
      data-testid="redemption-row-requested-at"
    >
      {formatStrictDistance(iso, toAppLocale(i18n.language), {
        addSuffix: true,
      })}
    </span>
  )
}

function computeInitials(name: string): string {
  if (!name) return "?"
  const parts = name.trim().split(/\s+/).slice(0, 2)
  return parts
    .map((p) => p.charAt(0).toUpperCase())
    .join("")
    .padEnd(1, "?")
}
